/**
 * Granular, persistent permissions (project-scoped).
 *
 * Every mutating tool call resolves to one {@link ActionKind} — edit / create /
 * delete / run — and each kind carries its own consent state: `ask` (prompt
 * every time), `allow` (always allow), or `deny` (always deny). Decisions made
 * via an
 * "always …" answer are written through to `~/.futurex/permissions.json` keyed
 * by the project root, so they survive process restarts and govern every later
 * action of the same kind in the same project.
 *
 * The flow is a single audited path implemented by {@link createPermissionGate}:
 *   action → kind resolution (ToolSpec.classify) → store lookup →
 *   prompt only if `ask` → persist on an always-* decision.
 *
 * The store lives under the user's home dir, NEVER inside the repository — a
 * cloned repo must not be able to grant itself permissions. All kinds default
 * to `ask`; `run` (shell commands) is never auto-defaulted to allow.
 */
import { existsSync, mkdirSync, readFileSync, writeFileSync } from 'node:fs';
import { dirname, resolve } from 'node:path';
import { ACTION_KINDS, type ActionKind } from '@fim/futurex-tools';
import { permissionsPath } from './config.js';

/** Consent state for one action kind. */
export type Consent = 'ask' | 'allow' | 'deny';

/** Consent per action kind for one project (missing kind = 'ask'). */
export type ProjectPermissions = Partial<Record<ActionKind, Consent>>;

/** What the gate is being asked to approve. */
export interface GateRequest {
  /** Tool name (e.g. `write_file`). */
  readonly tool: string;
  /** The permission type this call resolved to. */
  readonly kind: ActionKind;
  /** Human-readable one-line preview of the action. */
  readonly preview: string;
}

/** Permission decision for a mutating tool call: resolve true to allow. */
export type Gate = (req: GateRequest) => Promise<boolean>;

/** A user's answer to a permission prompt. */
export type GateAnswer = 'yes' | 'no' | 'always-allow' | 'always-deny';

/** UI hook that actually shows the prompt (chatbox modal / readline / deny-all). */
export type AskFn = (req: GateRequest) => Promise<GateAnswer>;

interface PermissionFile {
  version: 1;
  /** Keyed by normalized project root. */
  projects: Record<string, ProjectPermissions>;
}

/** Stable store key for a project root (case-normalized on Windows). */
export function projectKey(root: string): string {
  const abs = resolve(root);
  return process.platform === 'win32' ? abs.toLowerCase() : abs;
}

function isConsent(v: unknown): v is Consent {
  return v === 'ask' || v === 'allow' || v === 'deny';
}

function readStore(): PermissionFile {
  const path = permissionsPath();
  if (!existsSync(path)) return { version: 1, projects: {} };
  try {
    const raw = JSON.parse(readFileSync(path, 'utf8')) as Partial<PermissionFile>;
    const projects: Record<string, ProjectPermissions> = {};
    for (const [proj, perms] of Object.entries(raw.projects ?? {})) {
      const clean: ProjectPermissions = {};
      for (const kind of ACTION_KINDS) {
        const v = (perms as Record<string, unknown>)[kind];
        if (isConsent(v) && v !== 'ask') clean[kind] = v;
      }
      if (Object.keys(clean).length > 0) projects[proj] = clean;
    }
    return { version: 1, projects };
  } catch {
    // Unreadable/corrupt store → fail safe: everything back to 'ask'.
    return { version: 1, projects: {} };
  }
}

function writeStore(store: PermissionFile): void {
  const path = permissionsPath();
  mkdirSync(dirname(path), { recursive: true });
  writeFileSync(path, JSON.stringify(store, null, 2), { mode: 0o600 });
}

/** Load the consent map for a project (kinds absent from the store are 'ask'). */
export function loadProjectPermissions(root: string): Record<ActionKind, Consent> {
  const stored = readStore().projects[projectKey(root)] ?? {};
  const out = {} as Record<ActionKind, Consent>;
  for (const kind of ACTION_KINDS) out[kind] = stored[kind] ?? 'ask';
  return out;
}

/** Persist one consent decision for a project ('ask' clears the stored entry). */
export function setConsent(root: string, kind: ActionKind, consent: Consent): void {
  const store = readStore();
  const key = projectKey(root);
  const perms: ProjectPermissions = { ...(store.projects[key] ?? {}) };
  if (consent === 'ask') delete perms[kind];
  else perms[kind] = consent;
  if (Object.keys(perms).length === 0) delete store.projects[key];
  else store.projects[key] = perms;
  writeStore(store);
}

/** Drop every stored decision for a project (all kinds back to 'ask'). */
export function resetProjectPermissions(root: string): void {
  const store = readStore();
  delete store.projects[projectKey(root)];
  writeStore(store);
}

export interface PermissionGateOptions {
  /** Approve everything without prompting (dangerous; for scripts/tests). */
  readonly autoApprove?: boolean;
}

/**
 * THE permission gate: store lookup → prompt only when `ask` → persist on an
 * always-* answer. The store is re-read per request (it is one tiny JSON file),
 * so a decision immediately governs the very next action of the same kind — no
 * matter whether it came from a prompt, `/permissions set`, or another process.
 */
export function createPermissionGate(
  root: string,
  ask: AskFn,
  opts: PermissionGateOptions = {},
): Gate {
  return async (req) => {
    if (opts.autoApprove) return true;
    const consent = loadProjectPermissions(root)[req.kind]; // store lookup BEFORE any prompt
    if (consent === 'allow') return true;
    if (consent === 'deny') return false;
    const answer = await ask(req);
    if (answer === 'always-allow') {
      setConsent(root, req.kind, 'allow');
      return true;
    }
    if (answer === 'always-deny') {
      setConsent(root, req.kind, 'deny');
      return false;
    }
    return answer === 'yes';
  };
}

/** One line per kind for `/permissions` and `futurex permissions show`. */
export function describePermissions(root: string): string[] {
  const consents = loadProjectPermissions(root);
  const label: Record<Consent, string> = {
    ask: 'ask each time',
    allow: 'always allow',
    deny: 'always deny',
  };
  return ACTION_KINDS.map((kind) => `${kind.padEnd(6)} ${label[consents[kind]]}`);
}

/** Narrow user input to an {@link ActionKind}. */
export function parseActionKind(s: string): ActionKind | undefined {
  const v = s.trim().toLowerCase();
  return (ACTION_KINDS as readonly string[]).includes(v) ? (v as ActionKind) : undefined;
}

/** Narrow user input to a {@link Consent}. */
export function parseConsent(s: string): Consent | undefined {
  const v = s.trim().toLowerCase();
  if (v === 'allow' || v === 'always-allow') return 'allow';
  if (v === 'deny' || v === 'always-deny') return 'deny';
  if (v === 'ask') return 'ask';
  return undefined;
}
